import { useQuery } from '@tanstack/react-query';
import { cartService } from '@/services/cart';
import type { GetCartResult } from '@/services/cart';

/**
 * Query key factory for cart-related queries
 */
export const cartQueryKeys = {
  all: ['workflow-cart'] as const,
  activeAll: () => [...cartQueryKeys.all, 'active-all'] as const,
  cart: (customerId: string | null | undefined) =>
    [...cartQueryKeys.all, 'cart', customerId ?? 'no-customer'] as const,
};

/**
 * Hook to get all active carts.
 */
export const useAllActiveCarts = () => {
  return useQuery({
    queryKey: cartQueryKeys.activeAll(),
    queryFn: () => cartService.getAllActiveCarts(),
  });
};

/**
 * Hook to fetch the current user's workflow cart for a specific customer.
 * Returns cart metadata and full asset entities.
 * The frontend handles grouping assets by catalog_id for display.
 */
export const useWorkflowCart = (
  customerId: string | null | undefined,
  options?: {
    refetchInterval?: number | ((data: GetCartResult | undefined) => number | false);
  },
) => {
  return useQuery<GetCartResult>({
    queryKey: cartQueryKeys.cart(customerId),
    queryFn: () => cartService.getCart(customerId!),
    enabled: !!customerId,
    // Keep the cart data fresh but don't refetch too aggressively
    staleTime: 10_000, // 10 seconds
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      if (typeof options?.refetchInterval === 'function') {
        return options.refetchInterval(query.state.data);
      }
      return options?.refetchInterval ?? false;
    },
  });
};
